import { CreditCard, Menu, X, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { useAIAssistant } from '@/contexts/AIAssistantContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { Skeleton } from './ui/skeleton';

interface HeaderProps {
  onNavigate: (page: string) => void;
  currentPage: string;
}

export function Header({ onNavigate, currentPage }: HeaderProps) {
  const { openAIDialog } = useAIAssistant();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { data: settings, isLoading: loading } = useSiteSettings();

  const navItems = [
    { label: 'Alle Kreditkarten', value: 'home' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <button 
            onClick={() => onNavigate('home')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            aria-label="Zur Startseite"
          >
            {loading ? (
              <Skeleton className="h-8 w-28 rounded-lg" />
            ) : settings?.site_logo_url && !logoError ? (
            <img 
              src={settings.site_logo_url} 
              alt={settings?.site_name || 'Kreditkarten.de'} 
              className="h-8 w-auto object-contain"
              height="32"
              loading="eager"
              onError={() => {
                console.error('Header logo failed to load:', settings.site_logo_url);
                setLogoError(true);
              }}
            />
            ) : (
              <>
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="text-foreground font-medium">{settings?.site_name || 'Kreditkarten.de'}</span>
              </>
            )}
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-3">
            {navItems.map((item) => (
              <button
                key={item.value}
                onClick={() => onNavigate(item.value)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  currentPage === item.value
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* AI Wizard Button */}
            <Button
              onClick={() => openAIDialog()}
              className="gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Kartenassistent
            </Button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-accent"
            aria-label={mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-muted-foreground" />
            ) : (
              <Menu className="w-6 h-6 text-muted-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-border space-y-2">
            {navItems.map((item) => (
              <button
                key={item.value}
                onClick={() => {
                  onNavigate(item.value);
                  setMobileMenuOpen(false);
                }}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  currentPage === item.value
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                }`}
              >
                {item.label}
              </button>
            ))}
            
            {/* AI Wizard Button - Mobile */}
            <Button
              onClick={() => {
                openAIDialog();
                setMobileMenuOpen(false);
              }}
              className="w-full gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Kartenassistent
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
}
